import { InlineError, Skeleton } from '../../components/ui'
import { ReviewCard, SectionHeading, StaffCard } from '../../features/catalog'
import { isMissingResource, useGetReviewsQuery, useGetStaffQuery } from '../../services/catalogApi'
import { useTenant } from '../../tenant/useTenant'

const MAX_REVIEWS = 6

/** E3 — the people and what customers said about them. */
export function HomeSocial() {
  const { slug } = useTenant()
  const staff = useGetStaffQuery({ slug })
  const reviews = useGetReviewsQuery({ slug })

  // Reviews are the one endpoint that may not exist yet: a 404 means "nothing
  // to show", not a failure worth interrupting the page for.
  const reviewsUnavailable = isMissingResource(reviews.error)
  const reviewList = reviews.data ?? []

  return (
    <>
      <section className="container-page py-section-gap-md md:py-section-gap-lg">
        <SectionHeading eyebrow="Our team" title="Meet our specialists" align="center" />

        {staff.error ? (
          <InlineError error={staff.error} onRetry={staff.refetch} />
        ) : staff.isLoading ? (
          <div className="flex justify-center gap-8">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="flex w-40 flex-col items-center gap-3">
                <Skeleton shape="circle" className="h-32 w-32" />
                <Skeleton className="w-24" />
              </div>
            ))}
          </div>
        ) : staff.data?.length ? (
          // Centring has to happen on the wrapper, not the scroller: a flex row
          // with `justify-center` that overflows spills past *both* edges, and
          // the left overflow cannot be scrolled back to — the first specialists
          // were simply unreachable on a salon with a large team.
          <div className="flex md:justify-center">
            <ul className="flex max-w-full gap-8 overflow-x-auto pb-2 no-scrollbar">
              {staff.data.map((member) => (
                <li key={member.id} className="shrink-0">
                  <StaffCard staff={member} />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {!reviewsUnavailable && (reviews.isLoading || reviewList.length > 0 || reviews.error) && (
        <section className="bg-surface-container-low py-section-gap-md md:py-section-gap-lg">
          <div className="container-page">
            <SectionHeading eyebrow="Reviews" title="What our guests say" align="center" />

            {reviews.error ? (
              <InlineError error={reviews.error} onRetry={reviews.refetch} />
            ) : reviews.isLoading ? (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} shape="rect" className="h-44 w-full" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {reviewList.slice(0, MAX_REVIEWS).map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </>
  )
}
